import { useState, useEffect, useRef } from "react";
import {
    Building2, Store, Mail, Linkedin, RefreshCw, GitBranch,
    ShieldAlert, ChevronDown, ExternalLink, MapPin, Calendar, Users, Tag, X, Search,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getEstablishmentUseCaseCounts, UseCaseCounts, getUniqueTags } from "../../interfaces/signals";
import { getEstablishments } from "../../interfaces/establishments";
import { IEstablishment } from "../../interfaces/establishment";
import { useDebouncedEffect } from "../../hooks/useDebouncedEffect";

// ─── Use case definitions ─────────────────────────────────────────────────────
interface UseCase {
    id: keyof UseCaseCounts;
    label: string;
    description: string;
    icon: React.ElementType;
    estFilters: Record<string, string>;
}

const USE_CASES: UseCase[] = [
    {
        id: "multi_site_5plus",
        label: "Multi-site 5+",
        description: "Exploitants ayant 5 établissements ou plus",
        icon: Building2,
        estFilters: { signalType: "multi_site", founderEstablishmentsCount: "5+" },
    },
    {
        id: "multi_site_2_4",
        label: "Multi-site 2–4",
        description: "Exploitants ayant 2 à 4 établissements",
        icon: Building2,
        estFilters: { signalType: "multi_site", founderEstablishmentsCount: "2,3,4" },
    },
    {
        id: "restaurant_opening",
        label: "Nouvelles ouvertures",
        description: "Restaurants récemment ouverts",
        icon: Store,
        estFilters: { signalType: "restaurant_opening" },
    },
    {
        id: "opening_with_email",
        label: "Ouvertures avec email",
        description: "Nouvelles ouvertures avec email de contact disponible",
        icon: Mail,
        estFilters: { signalType: "restaurant_opening", contactFilters: "email" },
    },
    {
        id: "opening_with_linkedin",
        label: "Ouvertures avec LinkedIn",
        description: "Nouvelles ouvertures avec profil LinkedIn",
        icon: Linkedin,
        estFilters: { signalType: "restaurant_opening", contactFilters: "linkedin" },
    },
    {
        id: "owner_change",
        label: "Changements d'exploitant",
        description: "Restaurants ayant récemment changé de propriétaire",
        icon: RefreshCw,
        estFilters: { signalType: "restaurant_owner_change" },
    },
    {
        id: "franchise_signing",
        label: "Signataires franchise",
        description: "Nouveaux signataires d'une franchise",
        icon: GitBranch,
        estFilters: { signalType: "franchise_signing" },
    },
    {
        id: "hygiene_control",
        label: "Contrôles sanitaires",
        description: "Établissements ayant subi un contrôle sanitaire",
        icon: ShieldAlert,
        estFilters: { signalType: "hygiene_control" },
    },
];

// ─── Date range presets ────────────────────────────────────────────────────────
type Preset = "week" | "month" | "3months" | "6months" | "custom";

function getDateRange(preset: Preset, customFrom?: string, customTo?: string): { from: string; to: string } {
    const to = new Date();
    to.setHours(23, 59, 59, 999);
    const toStr = to.toISOString();

    if (preset === "custom") {
        return { from: customFrom ? new Date(customFrom).toISOString() : "", to: customTo ? new Date(customTo + "T23:59:59").toISOString() : toStr };
    }

    const from = new Date();
    if (preset === "week") from.setDate(from.getDate() - 7);
    else if (preset === "month") from.setMonth(from.getMonth() - 1);
    else if (preset === "3months") from.setMonth(from.getMonth() - 3);
    else if (preset === "6months") from.setMonth(from.getMonth() - 6);
    from.setHours(0, 0, 0, 0);

    return { from: from.toISOString(), to: toStr };
}

// ─── Tag picker ────────────────────────────────────────────────────────────────
interface TagPickerProps {
    allCategories: string[];
    allSubCategories: string[];
    selectedCategories: string[];
    selectedSubCategories: string[];
    onChangeCategories: (v: string[]) => void;
    onChangeSubCategories: (v: string[]) => void;
}

function TagPicker({
    allCategories, allSubCategories,
    selectedCategories, selectedSubCategories,
    onChangeCategories, onChangeSubCategories,
}: TagPickerProps) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const q = search.trim().toLowerCase();
    const filteredCats = allCategories.filter((c) => !q || c.toLowerCase().includes(q));
    const filteredSubs = allSubCategories.filter((c) => !q || c.toLowerCase().includes(q));

    const total = selectedCategories.length + selectedSubCategories.length;

    const toggleCat = (v: string) =>
        onChangeCategories(
            selectedCategories.includes(v) ? selectedCategories.filter((x) => x !== v) : [...selectedCategories, v]
        );

    const toggleSub = (v: string) =>
        onChangeSubCategories(
            selectedSubCategories.includes(v) ? selectedSubCategories.filter((x) => x !== v) : [...selectedSubCategories, v]
        );

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setOpen((p) => !p)}
                className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg border transition-colors duration-150 ${
                    total > 0
                        ? "bg-ink text-ink-inverse border-transparent"
                        : "bg-surface text-ink-muted border-border hover:border-border-strong"
                }`}
            >
                <Tag className="w-3.5 h-3.5" />
                {total > 0 ? `${total} tag${total > 1 ? "s" : ""}` : "Tags"}
                <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
                <div className="absolute left-0 top-full mt-1.5 w-80 rounded-2xl border border-border bg-surface shadow-float z-50 overflow-hidden">
                    <div className="p-2 border-b border-border">
                        <div className="flex items-center gap-2 px-2 py-1.5 bg-surface-sunken rounded-lg">
                            <Search className="w-3.5 h-3.5 text-ink-faint shrink-0" />
                            <input
                                autoFocus
                                type="text"
                                placeholder="Rechercher…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="flex-1 bg-transparent text-xs outline-none text-ink placeholder:text-ink-faint"
                            />
                            {search && (
                                <button onClick={() => setSearch("")}>
                                    <X className="w-3 h-3 text-ink-faint" />
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="overflow-y-auto max-h-64 p-2 flex flex-col gap-3">
                        {filteredCats.length > 0 && (
                            <div>
                                <p className="text-[10px] font-semibold text-ink-faint uppercase tracking-wide px-1 mb-1">Catégories</p>
                                <div className="flex flex-wrap gap-1">
                                    {filteredCats.map((c) => (
                                        <button
                                            key={c}
                                            onClick={() => toggleCat(c)}
                                            className={`text-xs px-2 py-0.5 rounded-full border transition-colors duration-150 ${
                                                selectedCategories.includes(c)
                                                    ? "bg-ink text-ink-inverse border-transparent"
                                                    : "border-border text-ink-muted hover:border-border-strong"
                                            }`}
                                        >
                                            {c}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {filteredSubs.length > 0 && (
                            <div>
                                <p className="text-[10px] font-semibold text-ink-faint uppercase tracking-wide px-1 mb-1">Sous-catégories</p>
                                <div className="flex flex-wrap gap-1">
                                    {filteredSubs.map((c) => (
                                        <button
                                            key={c}
                                            onClick={() => toggleSub(c)}
                                            className={`text-xs px-2 py-0.5 rounded-full border transition-colors duration-150 ${
                                                selectedSubCategories.includes(c)
                                                    ? "bg-ink text-ink-inverse border-transparent"
                                                    : "border-border text-ink-muted hover:border-border-strong"
                                            }`}
                                        >
                                            {c}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {filteredCats.length === 0 && filteredSubs.length === 0 && (
                            <p className="text-xs text-ink-faint text-center py-3">Aucun tag trouvé</p>
                        )}
                    </div>
                    {total > 0 && (
                        <div className="border-t border-border p-2">
                            <button
                                onClick={() => { onChangeCategories([]); onChangeSubCategories([]); }}
                                className="w-full text-xs text-ink-faint hover:text-ink transition-colors duration-150 py-1"
                            >
                                Effacer les tags
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── Active tag chips ──────────────────────────────────────────────────────────
function ActiveTagChips({
    categories, subCategories,
    onRemoveCat, onRemoveSub,
}: {
    categories: string[]; subCategories: string[];
    onRemoveCat: (v: string) => void; onRemoveSub: (v: string) => void;
}) {
    if (categories.length === 0 && subCategories.length === 0) return null;
    return (
        <div className="flex flex-wrap gap-1.5">
            {categories.map((c) => (
                <span key={c} className="flex items-center gap-1 text-xs bg-ink text-ink-inverse px-2 py-0.5 rounded-full">
                    {c}
                    <button onClick={() => onRemoveCat(c)}><X className="w-2.5 h-2.5" /></button>
                </span>
            ))}
            {subCategories.map((c) => (
                <span key={c} className="flex items-center gap-1 text-xs bg-ink/70 text-ink-inverse px-2 py-0.5 rounded-full">
                    {c}
                    <button onClick={() => onRemoveSub(c)}><X className="w-2.5 h-2.5" /></button>
                </span>
            ))}
        </div>
    );
}

// ─── Establishment result row ──────────────────────────────────────────────────
function EstablishmentRow({ est }: { est: IEstablishment }) {
    const navigate = useNavigate();
    const primaryPerson = est.peoples?.[0];
    const contact = primaryPerson?.name || null;

    const signalDate = est.signals?.[0]?.date;
    const date = signalDate
        ? new Date(signalDate).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })
        : est.createdAt
            ? new Date((est as any).createdAt).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })
            : null;

    const hasEmail = !!(est.email);
    const hasLinkedIn = !!(
        est.social_networks?.some((n) => n.platform?.toLowerCase() === "linkedin" && n.url) ||
        primaryPerson?.enrichments?.some((e: any) => e.type === "linkedin" && e.status === "approved")
    );

    const tagsList = [
        ...(est.categories ?? []),
        ...(est.subCategories ?? []),
    ].slice(0, 3);

    const founderCount = (est as any).founderSignalCount;

    return (
        <div
            className="flex items-center gap-4 px-4 py-3 hover:bg-surface-sunken/60 cursor-pointer border-b border-border last:border-0 transition-colors duration-150"
            onClick={() => est._id && navigate(`/establishments/${est._id}`)}
        >
            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-ink truncate">{est.name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                    {contact && <p className="text-xs text-ink-muted truncate">{contact}</p>}
                    {tagsList.length > 0 && (
                        <div className="flex gap-1">
                            {tagsList.map((t) => (
                                <span key={t} className="text-[10px] bg-surface-sunken text-ink-muted px-1.5 py-0.5 rounded-full">{t}</span>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="hidden sm:flex items-center gap-1 text-xs text-ink-faint shrink-0">
                <MapPin className="w-3 h-3" />
                <span className="max-w-[120px] truncate">{est.address?.city || "—"}</span>
            </div>

            {founderCount != null && founderCount > 1 && (
                <div className="flex items-center gap-1 text-xs text-info font-medium shrink-0">
                    <Users className="w-3 h-3" />
                    {founderCount}
                </div>
            )}

            <div className="flex items-center gap-1.5 shrink-0">
                {hasEmail && <Mail className="w-3.5 h-3.5 text-ink-muted" />}
                {hasLinkedIn && <Linkedin className="w-3.5 h-3.5 text-ink-muted" />}
            </div>

            {date && (
                <div className="hidden md:flex items-center gap-1 text-xs text-ink-faint shrink-0">
                    <Calendar className="w-3 h-3" />
                    {date}
                </div>
            )}

            <ExternalLink className="w-3.5 h-3.5 text-ink-faint shrink-0" />
        </div>
    );
}

// ─── Use case card ─────────────────────────────────────────────────────────────
interface UseCaseCardProps {
    uc: UseCase;
    count: number | null;
    isSelected: boolean;
    onSelect: () => void;
}

function UseCaseCard({ uc, count, isSelected, onSelect }: UseCaseCardProps) {
    const Icon = uc.icon;
    return (
        <button
            onClick={onSelect}
            className={`text-left rounded-2xl border p-4 flex flex-col gap-2 transition-colors duration-150
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 ${
                isSelected
                    ? "border-transparent bg-ink"
                    : "border-border bg-surface hover:border-border-strong"
            }`}
        >
            <div className="flex items-start justify-between gap-2">
                <div className={`p-1.5 rounded-lg ${isSelected ? "bg-white/10" : "bg-surface-sunken"}`}>
                    <Icon className={`w-4 h-4 ${isSelected ? "text-ink-inverse" : "text-ink-muted"}`} />
                </div>
                {count !== null ? (
                    <span className={`text-lg font-semibold leading-tight ${isSelected ? "text-ink-inverse" : "text-ink"}`}>
                        {count.toLocaleString("fr-FR")}
                    </span>
                ) : (
                    <span className={`text-sm ${isSelected ? "text-ink-inverse-muted" : "text-ink-faint"}`}>—</span>
                )}
            </div>
            <div>
                <p className={`text-sm font-semibold ${isSelected ? "text-ink-inverse" : "text-ink"}`}>
                    {uc.label}
                </p>
                <p className={`text-xs mt-0.5 ${isSelected ? "text-ink-inverse-muted" : "text-ink-muted"}`}>
                    {uc.description}
                </p>
            </div>
        </button>
    );
}

// ─── Main page ────────────────────────────────────────────────────────────────
const PRESETS: { key: Preset; label: string }[] = [
    { key: "week", label: "7 jours" },
    { key: "month", label: "1 mois" },
    { key: "3months", label: "3 mois" },
    { key: "6months", label: "6 mois" },
    { key: "custom", label: "Personnalisé" },
];

export default function UseCases({ embedded = false }: { embedded?: boolean }) {
    const [preset, setPreset] = useState<Preset>("month");
    const [customFrom, setCustomFrom] = useState("");
    const [customTo, setCustomTo] = useState("");

    const [allCategories, setAllCategories] = useState<string[]>([]);
    const [allSubCategories, setAllSubCategories] = useState<string[]>([]);
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [selectedSubCategories, setSelectedSubCategories] = useState<string[]>([]);

    const [counts, setCounts] = useState<UseCaseCounts | null>(null);
    const [selectedId, setSelectedId] = useState<keyof UseCaseCounts | null>(null);
    const [results, setResults] = useState<IEstablishment[]>([]);
    const [resultsPage, setResultsPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loadingCounts, setLoadingCounts] = useState(true);
    const [loadingResults, setLoadingResults] = useState(false);

    const dateRange = getDateRange(preset, customFrom, customTo);

    // Load available tags once
    useEffect(() => {
        getUniqueTags().then(({ categories, subCategories }) => {
            setAllCategories(categories);
            setAllSubCategories(subCategories);
        }).catch(() => {});
    }, []);

    const fetchCounts = (from: string, to: string, cats: string[], subs: string[]) => {
        if (!from || !to) return;
        setLoadingCounts(true);
        getEstablishmentUseCaseCounts(from, to, cats.length ? cats : undefined, subs.length ? subs : undefined)
            .then(setCounts)
            .catch(() => {})
            .finally(() => setLoadingCounts(false));
    };

    const fetchResults = (uc: UseCase, from: string, to: string, cats: string[], subs: string[], page: number, append = false) => {
        setLoadingResults(true);
        const extra: Record<string, string> = {};
        if (cats.length) extra.establishmentType = cats.join(",");
        if (subs.length) extra.concept = subs.join(",");

        getEstablishments({
            ...uc.estFilters,
            ...extra,
            from,
            to,
            sortBy: "signalDate",
            order: "desc",
            is_active: "true",
            page: String(page),
            limit: "20",
        })
            .then((data: any) => {
                const items: IEstablishment[] = data.establishments ?? [];
                setResults((prev) => append ? [...prev, ...items] : items);
                setHasMore(items.length === 20);
            })
            .catch(() => setResults([]))
            .finally(() => setLoadingResults(false));
    };

    // Reload counts whenever filters change
    useDebouncedEffect(() => {
        fetchCounts(dateRange.from, dateRange.to, selectedCategories, selectedSubCategories);
        if (selectedId) {
            const uc = USE_CASES.find((u) => u.id === selectedId);
            if (uc) {
                setResultsPage(1);
                fetchResults(uc, dateRange.from, dateRange.to, selectedCategories, selectedSubCategories, 1);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preset, customFrom, customTo, selectedCategories, selectedSubCategories]);

    const handleSelect = (uc: UseCase) => {
        if (selectedId === uc.id) {
            setSelectedId(null);
            setResults([]);
            return;
        }
        setSelectedId(uc.id);
        setResultsPage(1);
        fetchResults(uc, dateRange.from, dateRange.to, selectedCategories, selectedSubCategories, 1);
    };

    const loadMore = () => {
        const uc = USE_CASES.find((u) => u.id === selectedId);
        if (!uc) return;
        const next = resultsPage + 1;
        setResultsPage(next);
        fetchResults(uc, dateRange.from, dateRange.to, selectedCategories, selectedSubCategories, next, true);
    };

    const selectedUc = USE_CASES.find((u) => u.id === selectedId);
    const hasTagFilter = selectedCategories.length > 0 || selectedSubCategories.length > 0;

    const content = (
        <>
                {/* Header — hidden when embedded in another page */}
                {!embedded && (
                <div className="mb-6">
                    <h1 className="font-sans text-2xl font-semibold tracking-tight text-ink">Cas d'usage</h1>
                    <p className="text-sm text-ink-muted mt-1">
                        Segments prédéfinis basés sur les signaux détectés sur la période sélectionnée.
                    </p>
                </div>
                )}

                {/* Filter bar */}
                <div className="rounded-2xl border border-border bg-surface px-4 py-3 flex flex-wrap items-center gap-3 mb-2">
                    <span className="text-xs font-medium text-ink-muted shrink-0">Période</span>
                    <div className="flex items-center gap-1 rounded-full bg-surface-sunken p-1">
                        {PRESETS.map((p) => (
                            <button
                                key={p.key}
                                onClick={() => setPreset(p.key)}
                                className={`text-xs px-3 py-1.5 rounded-full transition-colors duration-150 ${
                                    preset === p.key ? "bg-surface shadow-card text-ink font-medium" : "text-ink-muted hover:text-ink"
                                }`}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                    {preset === "custom" && (
                        <div className="flex items-center gap-2">
                            <input
                                type="date"
                                value={customFrom}
                                onChange={(e) => setCustomFrom(e.target.value)}
                                className="text-xs border border-border rounded-lg px-2 py-1.5 text-ink bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                            />
                            <span className="text-xs text-ink-faint">→</span>
                            <input
                                type="date"
                                value={customTo}
                                onChange={(e) => setCustomTo(e.target.value)}
                                className="text-xs border border-border rounded-lg px-2 py-1.5 text-ink bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                            />
                        </div>
                    )}

                    <div className="w-px h-5 bg-border mx-1 shrink-0" />

                    <TagPicker
                        allCategories={allCategories}
                        allSubCategories={allSubCategories}
                        selectedCategories={selectedCategories}
                        selectedSubCategories={selectedSubCategories}
                        onChangeCategories={setSelectedCategories}
                        onChangeSubCategories={setSelectedSubCategories}
                    />

                    {loadingCounts && <span className="text-xs text-ink-faint ml-auto">Chargement…</span>}
                </div>

                {/* Active tag chips */}
                {hasTagFilter && (
                    <div className="mb-3 flex items-center gap-2 flex-wrap px-1">
                        <ActiveTagChips
                            categories={selectedCategories}
                            subCategories={selectedSubCategories}
                            onRemoveCat={(v) => setSelectedCategories((p) => p.filter((x) => x !== v))}
                            onRemoveSub={(v) => setSelectedSubCategories((p) => p.filter((x) => x !== v))}
                        />
                    </div>
                )}

                {/* Use case cards grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-5">
                    {USE_CASES.map((uc) => (
                        <UseCaseCard
                            key={uc.id}
                            uc={uc}
                            count={counts ? counts[uc.id] : null}
                            isSelected={selectedId === uc.id}
                            onSelect={() => handleSelect(uc)}
                        />
                    ))}
                </div>

                {/* Results panel */}
                {selectedUc && (
                    <div className="rounded-2xl border border-border bg-surface overflow-hidden">
                        <div className="px-4 py-3 flex items-center justify-between bg-ink">
                            <div className="flex items-center gap-2 flex-wrap">
                                <selectedUc.icon className="w-4 h-4 text-ink-inverse" />
                                <span className="text-sm font-semibold text-ink-inverse">{selectedUc.label}</span>
                                {counts && (
                                    <span className="text-xs text-ink-inverse-muted">
                                        · {counts[selectedUc.id].toLocaleString("fr-FR")} résultats
                                    </span>
                                )}
                                {hasTagFilter && (
                                    <div className="flex flex-wrap gap-1">
                                        {selectedCategories.map((c) => (
                                            <span key={c} className="text-[10px] bg-white/10 text-ink-inverse px-1.5 py-0.5 rounded-full">{c}</span>
                                        ))}
                                        {selectedSubCategories.map((c) => (
                                            <span key={c} className="text-[10px] bg-white/5 text-ink-inverse px-1.5 py-0.5 rounded-full">{c}</span>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <button onClick={() => { setSelectedId(null); setResults([]); }}>
                                <ChevronDown className="w-4 h-4 text-ink-inverse" />
                            </button>
                        </div>

                        {loadingResults && results.length === 0 ? (
                            <div className="p-6 text-center text-sm text-ink-faint">Chargement…</div>
                        ) : results.length === 0 ? (
                            <div className="p-6 text-center text-sm text-ink-faint">
                                Aucun résultat sur cette période{hasTagFilter ? " avec ces tags" : ""}.
                            </div>
                        ) : (
                            <>
                                <div className="divide-y divide-border">
                                    {results.map((est) => (
                                        <EstablishmentRow key={est._id} est={est} />
                                    ))}
                                </div>
                                {hasMore && (
                                    <div className="px-4 py-3 border-t border-border">
                                        <button
                                            onClick={loadMore}
                                            disabled={loadingResults}
                                            className="text-xs text-ink-muted hover:text-ink transition-colors duration-150 flex items-center gap-1"
                                        >
                                            {loadingResults ? "Chargement…" : "Voir plus"}
                                            {!loadingResults && <ChevronDown className="w-3.5 h-3.5" />}
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
        </>
    );

    if (embedded) return content;

    return (
        <div className="min-h-screen bg-canvas font-sans">
            <div className="mx-auto max-w-7xl px-4 md:px-8 py-8">
                {content}
            </div>
        </div>
    );
}
